import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faTag, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import LoadingSpinner from './LoadingSpinner';
import '../css/patch-notes-detail.css';
import i18n from "i18next";

const PatchNotesDetail = () => {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const [patchNote, setPatchNote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const formatDate = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return '';

        const localeMap = {
            'ua': 'uk',
            'en': 'en',
            'ko': 'ko',
            'fr': 'fr',
            'ru': 'ru',
        };
        const locale = localeMap[i18n.language] || 'en';

        return date.toLocaleString(locale, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const formatSimpleDate = (dateInput) => {
        if (!dateInput) return '';

        // Handle the object structure from the Symfony API response
        const isoDate = (typeof dateInput === 'object' && dateInput !== null && dateInput.date)
            ? dateInput.date
            : dateInput;

        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return '';
        const locale = i18n.language || 'en';
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    }

    const calculateReadTime = (text) => {
        if (!text) return `${t('patchNotes.readTime', { count: 1 })}`;

        const words = text.split(/\s+/).length;
        const readTime = Math.ceil(words / 200);

        const singular = t('patchNotes.readTimeSingular') || 'minute';
        const few = t('patchNotes.readTimeFew') || 'minutes';
        const many = t('patchNotes.readTimeMany') || 'minutes';

        const getPluralForm = (count, singular, few, many) => {
            const mod10 = count % 10;
            const mod100 = count % 100;

            if (mod10 === 1 && mod100 !== 11) {
                return singular;
            }
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
                return few;
            }
            return many;
        };

        const pluralForm = getPluralForm(readTime, singular, few, many);
        return `${readTime} ${pluralForm} ${t('patchNotes.forReading') || 'to read'}`;
    };

    useEffect(() => {
        const fetchPatchNote = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/patch-notes/${id}?lang=${i18n.language}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Patch note not found');
                }

                const data = await response.json();
                setPatchNote(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchPatchNote();
        }
    }, [id, i18n.language]);

    if (loading) {
        return <div className="patch-notes-detail-container"><LoadingSpinner /></div>;
    }

    if (!patchNote || error) {
        return (
            <div className="patch-notes-detail-container">
                <div className="error-message">
                    <h2>{t('patchNotes.errorTitle', 'Error')}</h2>
                    <p>{t('patchNotes.notFound', 'Patch Note Not Found')}</p>
                    <Link to="/" state={{ scrollTo: '#news-section' }} className="back-button">
                        <FontAwesomeIcon icon={faArrowLeft}/>
                        {t('patchNotes.backToPatchNotes') || 'Back'}
                    </Link>
                </div>
            </div>
        );
    }

    const bannerStyle = patchNote.image
        ? { backgroundImage: `url(${patchNote.image})` }
        : { backgroundColor: 'rgba(0, 0, 0, 0.6)' };

    return (
        <div className="patch-notes-detail-container">
            <div className="patch-notes-detail-banner">
                <div className="banner-background" style={bannerStyle}></div>
                <div className="banner-content">
                    {patchNote.version && (
                        <div className="banner-version">{patchNote.version}</div>
                    )}
                    <h1 className="banner-title">{patchNote.title}</h1>
                    <div className="banner-meta">
                        <span><FontAwesomeIcon icon={faCalendarAlt} /> {formatSimpleDate(patchNote.createdAt)}</span>
                        <span><FontAwesomeIcon icon={faClock} /> {calculateReadTime(patchNote.description)}</span>
                    </div>
                </div>
            </div>

            <div className="back-button-container">
                 <Link to="/" state={{ scrollTo: '#news-section' }} className="back-button">
                    <FontAwesomeIcon icon={faArrowLeft} />
                    {t('patchNotes.backToPatchNotes') || 'Back'}
                </Link>
            </div>

            <main className="patch-notes-detail-main">
                <div className="patch-notes-detail-body">
                    <div
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(patchNote.description)
                        }}
                    />
                </div>
            </main>
        </div>
    );
};

export default PatchNotesDetail;
